import { useLocation } from '@reach/router';
import axios from 'axios';
import { withPrefix } from 'gatsby';
import path from 'path';
import { createContext, FC, PropsWithChildren, useContext, useEffect } from 'react';

import isEnvDevelopment from '@/helpers/isEnvDevelopment';
import { useAllowedUris } from '@/hooks';
import { useModalContext } from '@/hooks/modal/useModal';
import { redirectWithAuthCode } from '@/services/auth.services';
import { eligibility } from '@/services/user.services';
import { setRedirectPath, setRedirectUri, setUserSession, useAppDispatch, useAppSelector } from '@/store';

export const SessionContext = createContext<null>(null);

export const useSessionContext = () => useContext(SessionContext);

export const UserSessionProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { userSession } = useAppSelector(state => state);
  const { user, isLoading, redirectPath, redirectUri, error } = userSession;

  const location = useLocation();
  const { origin, pathname, search } = location;
  const auth_code = new URLSearchParams(search).get('code');

  const allowedUris = useAllowedUris();
  const { toggleModal, setErrorInfo } = useModalContext();

  useEffect(() => {
    if (!allowedUris.isSelectAccountPage) {
      if (allowedUris.isProdListPage) {
        dispatch(setRedirectPath(withPrefix(`${path.join('/', allowedUris.isProdListPage.path)}`)));
      } else if (allowedUris.isOfficeAnywherePage) {
        dispatch(setRedirectPath(withPrefix(allowedUris.paths.officeAnywherePage)));
      } else if (allowedUris.isMicrosoft365ForBusinessBundlePage) {
        dispatch(
          setRedirectPath(withPrefix(`${path.join('/', allowedUris.isMicrosoft365ForBusinessBundlePage.path)}`))
        );
      } else {
        dispatch(setRedirectPath(withPrefix(`${path.join('/', pathname, search)}`)));
      }
    }
  }, []);

  useEffect(() => {
    if (redirectPath) {
      dispatch(setRedirectUri(`${origin}${path.join('/', redirectPath)}`));
    }
  }, [pathname, redirectPath]);

  useEffect(() => {
    (async () => {
      if (!isLoading && !user && !error && redirectUri && auth_code) {
        dispatch(setUserSession({ isLoading: true, error: null }));
      }
      if (isLoading && !user && !error && redirectUri && auth_code) {
        const res = await redirectWithAuthCode(
          `${origin}${path.join('/', allowedUris.paths.selectAccountPage)}`,
          auth_code
        );
        const stateUUID = new URLSearchParams(search).get('state');
        const sessionState = sessionStorage.getItem('state');
        if (!axios.isAxiosError(res) && (res.status === 201 || res.data === 'Created') && stateUUID === sessionState) {
          const eligibilityRes = await eligibility();

          if (!axios.isAxiosError(eligibilityRes) && eligibilityRes.status === 200) {
            const sessionId = res.headers['x-csrf-token'];
            dispatch(
              setUserSession({ isLoading: false, user: { ...eligibilityRes.data, csrfToken: sessionId }, error: null })
            );
            // NOTE: Correct response
            // navigate(withPrefix('marketplace/multiple-billing-account'), { state: { user: eligibilityRes.data } });
          } else {
            dispatch(
              setUserSession({
                isLoading: false,
                ...(axios.isAxiosError(eligibilityRes) && { error: eligibilityRes.response?.data }),
              })
            );
            setErrorInfo({
              ...(axios.isAxiosError(eligibilityRes) && {
                statusType: eligibilityRes.response?.data?.error,
                statusCode: eligibilityRes.response?.data?.code,
              }),
              requestUrl: 'eligibility',
            });
            toggleModal(true, 'Error');
          }
        } else {
          dispatch(setUserSession({ isLoading: false, ...(axios.isAxiosError(res) && { error: res.response?.data }) }));
          setErrorInfo({
            ...(axios.isAxiosError(res) && {
              statusType: res.response?.data?.error,
              statusCode: res.response?.status,
            }),
            requestUrl: 'login',
          });
          toggleModal(true, 'Error');
        }
      }
    })();
  }, [user, isLoading, redirectUri, auth_code, error]);

  useEffect(() => {
    if (error && user?.organizationId) {
      isEnvDevelopment() && console.log('error: ', error);
    }
  }, [error]);

  return <SessionContext.Provider value={null}>{children}</SessionContext.Provider>;
};
